import is from '@sindresorhus/is';

import type { MarkRange } from './comment-anchor-markup';
import { resolveCommentAnchorRange } from './comment-anchor-markup';
import { DESCRIPTION_DOCUMENT_SECTION_ID, workflowStepDocumentId } from './document/comments-document';
import type { Comment, CommentTarget } from './types';

export type CommentDocumentSection = {
    readonly id: string;
    readonly title: string;
    readonly text: string;
};

export type CommentDocumentSkill = {
    readonly id: string;
    readonly title: string;
};

export type CommentDocumentWorkflowStep = {
    readonly id: string;
    readonly title: string;
};

export type CommentDocumentState = {
    readonly sections: readonly CommentDocumentSection[];
    readonly skills: readonly CommentDocumentSkill[];
    readonly workflowSteps?: readonly CommentDocumentWorkflowStep[];
};

export type CommentClassificationLabels = {
    readonly title: string;
    readonly sectionFallback: string;
    readonly skillFallback: string;
    readonly workflowStepFallback?: string;
};

export type CommentMarkRange = MarkRange & {
    readonly commentId: string;
};

export type SectionCommentMarkRanges = {
    readonly sectionId: string;
    readonly ranges: readonly CommentMarkRange[];
};

export type HiddenCommentGroup = {
    readonly id: string;
    readonly label: string;
    readonly comments: readonly Comment[];
};

export type CommentClassification = {
    readonly activeComments: readonly Comment[];
    readonly hiddenCount: number;
    readonly hiddenGroups: readonly HiddenCommentGroup[];
    readonly sectionMarkRanges: readonly SectionCommentMarkRanges[];
};

type HiddenBucket = {
    readonly id: string;
    readonly label: string;
    readonly order: number;
    readonly comments: Comment[];
};

type ResolvedTarget = {
    readonly groupId: string;
    readonly groupLabel: string;
    readonly groupOrder: number;
};

type ClassificationContext = {
    readonly activeComments: Comment[];
    readonly hiddenBuckets: Map<string, HiddenBucket>;
    readonly labels: CommentClassificationLabels;
    readonly language: string;
    readonly markRangesBySectionId: Map<string, CommentMarkRange[]>;
    readonly sectionById: ReadonlyMap<string, CommentDocumentSection>;
    readonly sectionOrderById: ReadonlyMap<string, number>;
    readonly skillById: ReadonlyMap<string, CommentDocumentSkill>;
    readonly skillOrderById: ReadonlyMap<string, number>;
    readonly workflowStepById: ReadonlyMap<string, CommentDocumentWorkflowStep>;
    readonly workflowStepOrderById: ReadonlyMap<string, number>;
};

const sortByCreatedAtAscending = (first: Comment, second: Comment): number =>
    first.createdAt.localeCompare(second.createdAt);

const isTopLevelComment = (comment: Comment): boolean => is.nullOrUndefined(comment.parentId);

const pushHiddenComment = (buckets: Map<string, HiddenBucket>, target: ResolvedTarget, comment: Comment): void => {
    const bucket = buckets.get(target.groupId);
    if (is.undefined(bucket)) {
        buckets.set(target.groupId, {
            id: target.groupId,
            label: target.groupLabel,
            order: target.groupOrder,
            comments: [comment],
        });
        return;
    }
    bucket.comments.push(comment);
};

// The document keeps one internal vocabulary, so a work-task target is read as the document
// section or workflow step it names before grouping.
const toDocumentTarget = (target: CommentTarget): CommentTarget => {
    if (target.kind === 'description') {
        return { kind: 'description-section', sectionId: DESCRIPTION_DOCUMENT_SECTION_ID };
    }
    if (target.kind === 'workflow-step-title' || target.kind === 'workflow-step-description') {
        return { kind: 'workflow-step', workflowStepId: workflowStepDocumentId(target.stepIndex) };
    }
    return target;
};

const resolveHiddenTarget = (
    rawTarget: CommentTarget,
    labels: CommentClassificationLabels,
    sectionById: ReadonlyMap<string, CommentDocumentSection>,
    sectionOrderById: ReadonlyMap<string, number>,
    skillById: ReadonlyMap<string, CommentDocumentSkill>,
    skillOrderById: ReadonlyMap<string, number>,
    workflowStepById: ReadonlyMap<string, CommentDocumentWorkflowStep>,
    workflowStepOrderById: ReadonlyMap<string, number>,
): ResolvedTarget => {
    const target = toDocumentTarget(rawTarget);

    if (target.kind === 'title') {
        return { groupId: 'title', groupLabel: labels.title, groupOrder: 0 };
    }

    if (target.kind === 'description-section') {
        const section = sectionById.get(target.sectionId);
        if (is.undefined(section)) {
            return { groupId: 'section:fallback', groupLabel: labels.sectionFallback, groupOrder: 1_999 };
        }
        return {
            groupId: `section:${section.id}`,
            groupLabel: section.title,
            groupOrder: 1_000 + (sectionOrderById.get(section.id) ?? 0),
        };
    }

    if (target.kind === 'workflow-step') {
        const workflowStep = workflowStepById.get(target.workflowStepId);
        if (is.undefined(workflowStep)) {
            return {
                groupId: 'workflow-step:fallback',
                groupLabel: labels.workflowStepFallback ?? labels.sectionFallback,
                groupOrder: 3_999,
            };
        }
        return {
            groupId: `workflow-step:${workflowStep.id}`,
            groupLabel: workflowStep.title,
            groupOrder: 3_000 + (workflowStepOrderById.get(workflowStep.id) ?? 0),
        };
    }

    if (target.kind !== 'skill') {
        return { groupId: 'section:fallback', groupLabel: labels.sectionFallback, groupOrder: 1_999 };
    }

    const skill = skillById.get(target.skillId);
    if (is.undefined(skill)) {
        return { groupId: 'skill:fallback', groupLabel: labels.skillFallback, groupOrder: 2_999 };
    }
    return {
        groupId: `skill:${skill.id}`,
        groupLabel: skill.title,
        groupOrder: 2_000 + (skillOrderById.get(skill.id) ?? 0),
    };
};

const pushHiddenTargetComment = (context: ClassificationContext, comment: Comment): void =>
    pushHiddenComment(
        context.hiddenBuckets,
        resolveHiddenTarget(
            comment.target,
            context.labels,
            context.sectionById,
            context.sectionOrderById,
            context.skillById,
            context.skillOrderById,
            context.workflowStepById,
            context.workflowStepOrderById,
        ),
        comment,
    );

const pushSectionMarkRange = (
    rangesBySectionId: Map<string, CommentMarkRange[]>,
    sectionId: string,
    range: MarkRange,
    commentId: string,
): void => {
    const sectionRanges = rangesBySectionId.get(sectionId) ?? [];
    sectionRanges.push({ ...range, commentId });
    rangesBySectionId.set(sectionId, sectionRanges);
};

const classifyTopLevelComment = (context: ClassificationContext, comment: Comment): void => {
    if (!is.nullOrUndefined(comment.resolution)) {
        pushHiddenTargetComment(context, comment);
        return;
    }

    if (comment.sourceLanguage !== context.language) {
        pushHiddenTargetComment(context, comment);
        return;
    }

    const target = toDocumentTarget(comment.target);

    if (target.kind === 'title') {
        context.activeComments.push(comment);
        return;
    }

    if (target.kind === 'skill') {
        if (context.skillById.has(target.skillId)) {
            context.activeComments.push(comment);
            return;
        }
        pushHiddenTargetComment(context, comment);
        return;
    }

    if (target.kind === 'workflow-step') {
        if (context.workflowStepById.has(target.workflowStepId)) {
            context.activeComments.push(comment);
            return;
        }
        pushHiddenTargetComment(context, comment);
        return;
    }

    const section = target.kind === 'description-section' ? context.sectionById.get(target.sectionId) : undefined;
    const range = is.undefined(section) ? null : resolveCommentAnchorRange(comment.anchor, section.text);
    if (!is.null(range) && !is.undefined(section)) {
        context.activeComments.push(comment);
        pushSectionMarkRange(context.markRangesBySectionId, section.id, range, comment.id);
        return;
    }

    pushHiddenTargetComment(context, comment);
};

export const classifyComments = ({
    comments,
    documentState,
    labels,
    language,
}: {
    readonly comments: readonly Comment[];
    readonly documentState: CommentDocumentState;
    readonly labels: CommentClassificationLabels;
    readonly language: string;
}): CommentClassification => {
    const sectionById = new Map(documentState.sections.map((section) => [section.id, section]));
    const sectionOrderById = new Map(documentState.sections.map((section, index) => [section.id, index]));
    const skillById = new Map(documentState.skills.map((skill) => [skill.id, skill]));
    const skillOrderById = new Map(documentState.skills.map((skill, index) => [skill.id, index]));
    const workflowStepById = new Map((documentState.workflowSteps ?? []).map((step) => [step.id, step]));
    const workflowStepOrderById = new Map((documentState.workflowSteps ?? []).map((step, index) => [step.id, index]));
    const activeComments: Comment[] = [];
    const hiddenBuckets = new Map<string, HiddenBucket>();
    const markRangesBySectionId = new Map<string, CommentMarkRange[]>();
    const context: ClassificationContext = {
        activeComments,
        hiddenBuckets,
        labels,
        language,
        markRangesBySectionId,
        sectionById,
        sectionOrderById,
        skillById,
        skillOrderById,
        workflowStepById,
        workflowStepOrderById,
    };

    for (const comment of comments.filter(isTopLevelComment)) {
        classifyTopLevelComment(context, comment);
    }

    const hiddenGroups = [...hiddenBuckets.values()]
        .map((bucket) => ({
            id: bucket.id,
            label: bucket.label,
            order: bucket.order,
            comments: bucket.comments.slice().sort(sortByCreatedAtAscending),
        }))
        .sort((first, second) => first.order - second.order)
        .map(({ comments: groupComments, id, label }) => ({ id, label, comments: groupComments }));

    return {
        activeComments: activeComments.slice().sort(sortByCreatedAtAscending),
        hiddenCount: hiddenGroups.reduce((count, group) => count + group.comments.length, 0),
        hiddenGroups,
        sectionMarkRanges: [...markRangesBySectionId.entries()].map(([sectionId, ranges]) => ({
            sectionId,
            ranges: ranges.slice().sort((first, second) => first.start - second.start),
        })),
    };
};
